/**
 * WebSocketService - Real-time multiplayer communication
 * date_of_change: 2025-09-24
 *
 * Manages Socket.IO client connection to the backend for real-time multiplayer features.
 * Handles connection lifecycle, authentication, and automatic reconnection.
 */

import { io } from 'socket.io-client';
import backendUrlResolver from '../utils/BackendUrlResolver';

const SUPERSEDING_FULL_MAP_UPDATE_TYPES = new Set([
  'map_update',
  'token_ownership',
  'player_presence',
  'fog_update',
  'drawing_update',
]);

export class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnecting = false;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds

    // Track current backend for consistency
    // @date_of_change: 2025-09-25 - Added for backend consistency
    this.currentBackendBase = null;

    // @date_of_change: 2025-10-01 - Store auth data for reconnections to preserve GM role
    this.storedAuthData = null;
    // Clerk tokens are short-lived. The auth bridge supplies a fresh token for
    // every replacement socket so a backend restart cannot reconnect the
    // transport as an unauthenticated participant. date_of_change: 2026-08-25
    this.authTokenProvider = null;

    // Every caller shares the same transport attempt. The previous separate
    // resolve-only "ready" promise could remain pending forever when the
    // outer watchdog expired before Socket.IO's own timeout, trapping the
    // BattleRoom in Reconnecting. date_of_change: 2026-08-29
    this.connectionAttemptPromise = null;
    this.cancelConnectionAttempt = null;
    // WHAT: Keep the watchdog beyond Socket.IO's 15-second handshake timeout.
    // WHY: Socket.IO should report the useful connection error first, while
    // this still guarantees that a silent attempt settles and can be retried.
    // date_of_change: 2026-08-29
    this.connectionAttemptTimeoutMs = 20000;

    // Event listeners registry
    this.eventListeners = new Map();

    // Connection state callbacks
    this.connectionCallbacks = {
      onConnect: [],
      onDisconnect: [],
      onReconnect: [],
      onError: []
    };

    // Revisioned mutations are sent one at a time. The queue survives a
    // transport reconnect, and mutation IDs make an ACK-loss retry safe.
    // date_of_change: 2026-08-13
    this.pendingMutations = [];
    this.mutationInFlight = null;
    this.mutationAckTimer = null;
    // Production writes can include revision checkpoints and projected scene
    // state. Give the authoritative save time to finish before an idempotent
    // retry; the server also coalesces any retry that still overlaps it.
    // date_of_change: 2026-08-25
    this.mutationAckTimeoutMs = 60000;
    this.mutationIdleWaiters = new Set();
    // Actor-rich authoritative join snapshots can exceed the old 15-second
    // window in production. A premature timeout left the context outside
    // multiplayer mode even though the server acknowledged moments later.
    // date_of_change: 2026-08-25
    this.joinAckTimeoutMs = 60000;
    this.sessionRevisions = new Map();
    this.currentSessionId = null;
    this.reconnectTimer = null;
    this.intentionalDisconnect = false;
  }

  /**
   * Connect to the WebSocket server with backend consistency
   * Ensures WebSocket uses the same base as REST API
   * @date_of_change: 2026-08-29 - Share one settle-once attempt across callers
   * @param {Object} auth - Authentication data (user token, etc.)
   */
  connect(auth = {}) {
    if (this.connectionAttemptPromise) {
      console.log('[WebSocket] Connection already in progress');
      return this.connectionAttemptPromise;
    }

    const attempt = this.establishConnection(auth);
    this.connectionAttemptPromise = attempt;
    const clearAttempt = () => {
      if (this.connectionAttemptPromise === attempt) {
        this.connectionAttemptPromise = null;
      }
    };
    attempt.then(clearAttempt, clearAttempt);
    return attempt;
  }

  async establishConnection(auth = {}) {
    try {
      this.intentionalDisconnect = false;
      // Get backend URL and derive WebSocket URL
      const backendBase = await backendUrlResolver.getBackendUrl();

      // Check if we need to reconnect due to backend change
      if (this.currentBackendBase === backendBase && this.socket?.connected) {
        console.log('[WebSocket] Already connected to correct backend:', backendBase);
        return Promise.resolve();
      }

      this.isConnecting = true;

      // Disconnect existing socket if backend changed
      if (this.socket && this.currentBackendBase !== backendBase) {
        console.log('[WebSocket] Backend changed, disconnecting existing socket');
        this.disconnect({ preserveSession: true });
        this.intentionalDisconnect = false;
      }

      this.currentBackendBase = backendBase;
      // @date_of_change: 2025-10-01 - Store auth data for reconnections to preserve GM role
      this.storedAuthData = { ...auth };
      const wsUrl = backendUrlResolver.httpToWs(backendBase);
      console.log('[WebSocket] Connecting to WebSocket:', wsUrl, 'from backend:', backendBase);

      // Create socket connection with proper configuration
      const attemptSocket = io(wsUrl, {
        // Connection options with secure protocol handling
        // @date_of_change: 2025-09-25 - Enhanced for production safety
        transports: ['websocket', 'polling'], // Try WebSocket first, fallback to polling
        timeout: 15000, // Increased connection timeout to 15 seconds
        forceNew: true,
        upgrade: true,
        rememberUpgrade: false, // Always test transport on reconnect

        // Authentication - pass token in both auth object and headers for backend compatibility
        // @date_of_change: 2025-09-26 - Added token to auth for WebSocket role detection
        // @date_of_change: 2025-10-01 - Store auth for reconnections to preserve GM role
        auth: {
          ...auth,
          timestamp: Date.now(),
          token: auth.token // Ensure token is available for backend authentication
        },

        // @date_of_change: 2025-10-01 - Disable automatic reconnection to handle auth properly
        // Automatic reconnection doesn't preserve authentication, causing GM->Player role corruption
        reconnection: false, // We'll handle reconnection manually to preserve auth

        // CORS settings with production considerations
        withCredentials: false,

        // Ensure proper protocol handling for HTTPS/WSS
        secure: wsUrl.startsWith('wss:'),

        // Additional headers for Railway/ngrok and authentication
        // @date_of_change: 2025-09-26 - Added authorization header for WebSocket authentication
        extraHeaders: {
          'ngrok-skip-browser-warning': 'true',
          ...(auth.token && { 'authorization': `Bearer ${auth.token}` })
        }
      });
      this.socket = attemptSocket;

      // Set up event handlers
      this.setupEventHandlers();

      // A manual reconnect creates a new Socket.IO instance. Reattach every
      // application listener registered through on() before the room rejoins.
      this.eventListeners.forEach((callbacks, event) => {
        callbacks.forEach((callback) => attemptSocket.on(event, callback));
      });

      // Wait for exactly one terminal result. A timeout disposes the stalled
      // socket and clears isConnecting before reconnectWithAuth schedules its
      // next attempt. date_of_change: 2026-08-29
      return new Promise((resolve, reject) => {
        let settled = false;

        const cleanupAttemptListeners = () => {
          attemptSocket.off('connect', handleConnect);
          attemptSocket.off('connect_error', handleConnectError);
          if (this.cancelConnectionAttempt === cancelAttempt) {
            this.cancelConnectionAttempt = null;
          }
        };

        const settleFailure = (error) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          cleanupAttemptListeners();
          if (this.socket === attemptSocket) {
            this.isConnecting = false;
            this.isConnected = false;
            // The failed socket is transport-scoped and must not deliver a
            // late lifecycle event into the replacement attempt.
            attemptSocket.removeAllListeners?.();
            attemptSocket.disconnect?.();
            this.socket = null;
          }
          reject(error);
        };

        const handleConnect = () => {
          if (settled || this.socket !== attemptSocket) return;
          settled = true;
          clearTimeout(timeout);
          cleanupAttemptListeners();
          this.isConnecting = false;
          this.isConnected = true;
          this.reconnectAttempts = 0;
          console.log('[WebSocket] Connected successfully');

          // Notify connection callbacks
          this.connectionCallbacks.onConnect.forEach(callback => {
            try { callback(); } catch (error) { console.error('Connection callback error:', error); }
          });

          resolve();
        };

        const handleConnectError = (error) => {
          console.error('[WebSocket] Connection failed:', error);
          settleFailure(error);
        };

        const cancelAttempt = (reason = 'Connection cancelled') => {
          const error = new Error(reason);
          error.code = 'CONNECTION_CANCELLED';
          settleFailure(error);
        };

        const timeout = setTimeout(() => {
          settleFailure(new Error('Connection timeout'));
        }, this.connectionAttemptTimeoutMs);

        this.cancelConnectionAttempt = cancelAttempt;
        attemptSocket.once('connect', handleConnect);
        attemptSocket.once('connect_error', handleConnectError);
      });

    } catch (error) {
      this.isConnecting = false;
      console.error('[WebSocket] Connect error:', error);
      throw error;
    }
  }

  /**
   * Set up Socket.IO event handlers
   */
  setupEventHandlers() {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('[WebSocket] Connected, ID:', this.socket.id);
      this.isConnected = true;
      this.reconnectAttempts = 0;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[WebSocket] Disconnected:', reason);
      this.isConnected = false;
      // Socket.IO room membership belongs to one transport connection. Keep
      // queued mutations and revisions, but require the replacement socket to
      // receive a fresh join ACK before any write can flush.
      // date_of_change: 2026-08-25
      this.currentSessionId = null;
      // An ACK can be lost even when the server committed the mutation. Keep
      // the same mutationId for the reconnect retry so the server can answer
      // idempotently instead of applying the change twice.
      // date_of_change: 2026-08-13
      this.requeueInFlightMutation();

      // @date_of_change: 2025-10-01 - Manual reconnection with authentication to preserve GM role
      // Handle specific disconnection reasons that should trigger authenticated reconnection
      if (!this.intentionalDisconnect && reason !== 'io client disconnect') {
        if (this.storedAuthData && this.storedAuthData.token) {
          console.log('[WebSocket] Initiating authenticated reconnection to preserve role...');
          this.scheduleAuthenticatedReconnect(2000);
        } else {
          console.warn('[WebSocket] Cannot reconnect - no stored auth data');
        }
      }

      // Notify disconnect callbacks
      this.connectionCallbacks.onDisconnect.forEach(callback => {
        try { callback(reason); } catch (error) { console.error('Disconnect callback error:', error); }
      });
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('[WebSocket] Reconnected after', attemptNumber, 'attempts');
      this.isConnected = true;

      // Notify reconnect callbacks
      this.connectionCallbacks.onReconnect.forEach(callback => {
        try { callback(attemptNumber); } catch (error) { console.error('Reconnect callback error:', error); }
      });
    });

    this.socket.on('reconnect_error', (error) => {
      this.reconnectAttempts++;
      console.error(`[WebSocket] Reconnect attempt ${this.reconnectAttempts} failed:`, error);

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('[WebSocket] Max reconnect attempts reached');
        this.connectionCallbacks.onError.forEach(callback => {
          try { callback(new Error('Max reconnect attempts reached')); } catch (error) { console.error('Error callback error:', error); }
        });
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('[WebSocket] Connection error:', error);
      this.isConnected = false;

      // Notify error callbacks
      this.connectionCallbacks.onError.forEach(callback => {
        try { callback(error); } catch (error) { console.error('Error callback error:', error); }
      });
    });

    // Generic error handler
    this.socket.on('error', (error) => {
      console.error('[WebSocket] Socket error:', error);

      // The backend also uses the Socket.IO `error` event for rejected
      // application commands (rate limits, policy failures, validation). Those
      // do not disconnect the transport and must not make the UI report a
      // false offline state. Domain listeners still receive the event through
      // the normal event-listener registry. date_of_change: 2026-08-25
      if (!this.socket?.connected || !this.isConnected) {
        this.connectionCallbacks.onError.forEach(callback => {
          try { callback(error); } catch (callbackError) { console.error('Error callback error:', callbackError); }
        });
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect({ preserveSession = false } = {}) {
    this.intentionalDisconnect = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.cancelConnectionAttempt) {
      this.cancelConnectionAttempt('Connection cancelled by disconnect');
    }
    if (this.socket) {
      console.log('[WebSocket] Disconnecting...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.isConnecting = false;
      this.requeueInFlightMutation();
    }
    // Even a preservation reconnect replaces the transport and therefore its
    // Socket.IO room membership. The context retains the desired room and
    // explicitly rejoins it after the new connection is ready.
    // date_of_change: 2026-08-25
    this.currentSessionId = null;
    // Authentication teardown must not leak one user's queued room changes
    // into a later login. Internal transport/backend reconnects opt into
    // preserving the session explicitly.
    // date_of_change: 2026-08-13
    if (!preserveSession) {
      const abandoned = [this.mutationInFlight, ...this.pendingMutations].filter(Boolean);
      const disconnectError = new Error('The BattleRoom session ended before queued changes were acknowledged.');
      disconnectError.code = 'SESSION_DISCONNECTED';
      this.rejectMutationEntries(abandoned, disconnectError);
      this.pendingMutations = [];
      this.mutationInFlight = null;
      this.sessionRevisions.clear();
      this.notifyMutationQueue();
    }
  }

  /**
   * Reconnect with stored authentication data to preserve role
   * @date_of_change: 2025-10-01 - Added to fix GM role persistence
   */
  async reconnectWithAuth() {
    if (!this.storedAuthData) {
      console.error('[WebSocket] Cannot reconnect - no stored auth data');
      return false;
    }

    try {
      // Increment reconnect attempts for tracking
      this.reconnectAttempts++;

      if (this.reconnectAttempts > this.maxReconnectAttempts) {
        console.error('[WebSocket] Max reconnect attempts reached - triggering session cleanup');

        // @date_of_change: 2025-10-01 - Trigger session cleanup only after all reconnection attempts fail
        // Dispatch event to notify WebSocketContext to clean up session state
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('websocket-reconnection-failed', {
            detail: { sessionId: this.currentSessionId, attempts: this.reconnectAttempts }
          }));
        }

        return false;
      }

      console.log(`[WebSocket] Authenticated reconnect attempt ${this.reconnectAttempts}`);

      const freshToken = this.authTokenProvider
        ? await this.authTokenProvider()
        : this.storedAuthData.token;
      if (!freshToken) {
        throw new Error('A current authentication token is required to reconnect.');
      }

      // Preserve stable profile metadata, but replace the expiring credential
      // before the new Socket.IO handshake. date_of_change: 2026-08-25
      await this.connect({ ...this.storedAuthData, token: freshToken });

      // Reset attempts on successful reconnection
      this.reconnectAttempts = 0;

      return true;
    } catch (error) {
      if (this.intentionalDisconnect || error?.code === 'CONNECTION_CANCELLED') {
        console.log('[WebSocket] Authenticated reconnect cancelled');
        return false;
      }
      console.error('[WebSocket] Authenticated reconnect failed:', error);

      // Bound retries, use one tracked timer, and honor an intentional logout
      // while preserving the authenticated session and queued mutations.
      // date_of_change: 2026-08-13
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        const delay = Math.min(this.reconnectDelay * (2 ** this.reconnectAttempts), this.maxReconnectDelay);
        console.log(`[WebSocket] Retrying in ${delay}ms...`);
        this.scheduleAuthenticatedReconnect(delay);
      } else if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('websocket-reconnection-failed', {
          detail: { sessionId: this.currentSessionId, attempts: this.reconnectAttempts }
        }));
      }

      return false;
    }
  }

  scheduleAuthenticatedReconnect(delay = this.reconnectDelay) {
    if (this.intentionalDisconnect || !this.storedAuthData?.token) return false;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnectWithAuth();
    }, Math.max(0, Number(delay) || this.reconnectDelay));
    return true;
  }

  setAuthTokenProvider(provider) {
    this.authTokenProvider = typeof provider === 'function' ? provider : null;
  }

  /**
   * Add event listener
   * @param {string} event - Event name
   * @param {Function} callback - Event callback
   */
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event).add(callback);

    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Event callback
   */
  off(event, callback) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).delete(callback);
    }

    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  /**
   * Emit event to server
   * @param {string} event - Event name
   * @param {*} data - Event data
   * @param {Function} ack - Acknowledgment callback
   */
  emit(event, data, ack) {
    if (!this.socket || !this.isConnected) {
      console.warn(`[WebSocket] Cannot emit '${event}' - not connected`);
      return false;
    }

    try {
      if (ack) {
        this.socket.emit(event, data, ack);
      } else {
        this.socket.emit(event, data);
      }
      return true;
    } catch (error) {
      console.error(`[WebSocket] Error emitting '${event}':`, error);
      return false;
    }
  }

  /**
   * Queue an authoritative mutation and serialize it against the latest server
   * revision. Later queued mutations receive the revision from the prior ACK,
   * preventing rapid map/token changes from racing one another.
   * date_of_change: 2026-08-13
   */
  emitMutation(event, data) {
    return this.queueMutation(event, data);
  }

  /** Queue a mutation and resolve with its authoritative ACK. */
  emitMutationRequest(event, data) {
    return new Promise((resolve, reject) => {
      const queued = this.queueMutation(event, data, { resolve, reject });
      if (!queued) reject(new Error(`Unable to queue ${event}.`));
    });
  }

  queueMutation(event, data, callbacks = {}) {
    const sessionId = String(data?.sessionId || this.currentSessionId || '');
    const mutationId = String(data?.mutationId || '');
    if (!sessionId || !mutationId) {
      console.error(`[WebSocket] Cannot queue '${event}' without sessionId and mutationId`);
      return false;
    }
    if (this.pendingMutations.some((entry) => entry.mutationId === mutationId)
      || this.mutationInFlight?.mutationId === mutationId) {
      return true;
    }

    // Roster add/delete commands must each commit their explicit actor ID.
    // Coalescing them can lose actors across an intervening server echo.
    // Rule: authoritative roster membership. date_of_change: 2026-11-05
    // These other editor/setup mutations carry a complete map snapshot. While one
    // save is in flight, only the newest unsent snapshot is authoritative; the
    // intermediate snapshots would merely rewrite progressively stale copies
    // of the same scene. Mechanical movement remains strictly sequential.
    // date_of_change: 2026-08-25
    const updateType = String(data?.mapState?.updateType || '').trim().toLowerCase();
    if (event === 'updateMap' && SUPERSEDING_FULL_MAP_UPDATE_TYPES.has(updateType)) {
      const superseded = this.pendingMutations.filter((entry) => (
        entry.sessionId === sessionId
        && entry.event === 'updateMap'
        && SUPERSEDING_FULL_MAP_UPDATE_TYPES.has(String(entry.data?.mapState?.updateType || '').trim().toLowerCase())
      ));
      if (superseded.length > 0) {
        this.pendingMutations = this.pendingMutations.filter((entry) => !superseded.includes(entry));
        const supersededError = new Error('A newer complete BattleRoom map state replaced this queued update before transmission.');
        supersededError.code = 'MUTATION_SUPERSEDED';
        this.rejectMutationEntries(superseded, supersededError);
      }
    }
    this.pendingMutations.push({ event, data: { ...data, sessionId, mutationId }, sessionId, mutationId, ...callbacks });
    this.notifyMutationQueue();
    this.flushMutationQueue();
    return true;
  }

  /** Read-only request/ACK channel; it never enters the mutation queue. */
  emitRequest(event, data, timeoutMs = 15000) {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected || !this.isConnected) {
        reject(new Error('BattleRoom is offline. Reconnect before trying again.'));
        return;
      }
      const timeout = setTimeout(() => reject(new Error(`${event} timed out.`)), timeoutMs);
      this.socket.emit(event, data, (response = {}) => {
        clearTimeout(timeout);
        if (response.ok === false) {
          const error = new Error(response.message || `Failed to process ${event}.`);
          error.code = response.code;
          error.response = response;
          reject(error);
          return;
        }
        resolve(response);
      });
    });
  }

  notifyMutationQueue() {
    this.settleMutationIdleWaiters();
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('realtime-mutation-queue', {
      detail: {
        pendingCount: this.pendingMutations.length + (this.mutationInFlight ? 1 : 0),
        connected: Boolean(this.socket?.connected && this.isConnected),
      }
    }));
  }

  hasPendingMutationForSession(sessionId) {
    const normalizedSessionId = String(sessionId || '');
    return this.mutationInFlight?.sessionId === normalizedSessionId
      || this.pendingMutations.some((entry) => entry.sessionId === normalizedSessionId);
  }

  settleMutationIdleWaiters() {
    [...this.mutationIdleWaiters].forEach((waiter) => {
      if (this.hasPendingMutationForSession(waiter.sessionId)) return;
      clearTimeout(waiter.timer);
      this.mutationIdleWaiters.delete(waiter);
      const revision = this.sessionRevisions.get(waiter.sessionId);
      if (!Number.isSafeInteger(revision)) {
        const error = new Error('The BattleRoom has not received an authoritative revision yet.');
        error.code = 'BATTLE_ROOM_NOT_READY';
        waiter.reject(error);
        return;
      }
      waiter.resolve(revision);
    });
  }

  /**
   * Resolve only when every earlier mutation for this room has acknowledged.
   * Creature dice and action outcomes can then be calculated from the matching
   * authoritative snapshot instead of a revision that is already queued to be
   * replaced. date_of_change: 2026-09-08
   */
  waitForMutationQueueIdle(sessionId, timeoutMs = this.mutationAckTimeoutMs + 5000) {
    const normalizedSessionId = String(sessionId || '');
    if (!normalizedSessionId) {
      const error = new Error('Join a live BattleRoom before resolving an action.');
      error.code = 'BATTLE_ROOM_NOT_READY';
      return Promise.reject(error);
    }

    if (!this.hasPendingMutationForSession(normalizedSessionId)) {
      const revision = this.sessionRevisions.get(normalizedSessionId);
      if (Number.isSafeInteger(revision)) return Promise.resolve(revision);
      const error = new Error('The BattleRoom has not received an authoritative revision yet.');
      error.code = 'BATTLE_ROOM_NOT_READY';
      return Promise.reject(error);
    }

    return new Promise((resolve, reject) => {
      const waiter = { sessionId: normalizedSessionId, resolve, reject, timer: null };
      waiter.timer = setTimeout(() => {
        this.mutationIdleWaiters.delete(waiter);
        const error = new Error('The BattleRoom mutation queue did not settle before action resolution.');
        error.code = 'BATTLE_ROOM_NOT_READY';
        reject(error);
      }, timeoutMs);
      this.mutationIdleWaiters.add(waiter);
    });
  }

  rejectMutationEntries(entries, error) {
    entries.forEach((entry) => entry?.reject?.(error));
  }

  requeueInFlightMutation() {
    const entry = this.mutationInFlight;
    if (!entry) return;
    if (this.mutationAckTimer) {
      clearTimeout(this.mutationAckTimer);
      this.mutationAckTimer = null;
    }
    this.mutationInFlight = null;
    if (!this.pendingMutations.some((candidate) => candidate.mutationId === entry.mutationId)) {
      this.pendingMutations.unshift(entry);
    }
    this.notifyMutationQueue();
  }

  setSessionRevision(sessionId, revision) {
    const normalized = Number(revision);
    if (sessionId && Number.isSafeInteger(normalized) && normalized >= 0) {
      this.sessionRevisions.set(String(sessionId), normalized);
    }
  }

  flushMutationQueue() {
    if (!this.socket?.connected || !this.isConnected || !this.currentSessionId || this.mutationInFlight) return;
    const index = this.pendingMutations.findIndex((entry) => entry.sessionId === String(this.currentSessionId));
    if (index < 0) return;
    const entry = this.pendingMutations.splice(index, 1)[0];
    const latestAcknowledgedRevision = this.sessionRevisions.get(entry.sessionId);
    // A resolved creature action contains dice and outcomes produced from one
    // exact snapshot. Never silently rebase that payload behind an earlier
    // queued write; pin the transport precondition to its resolution revision
    // so a rare intervening change takes the normal conflict/recovery path.
    // date_of_change: 2026-09-08
    const resolutionRevision = entry.data?.resolutionRevision;
    const expectedRevision = entry.event === 'creature:action:commit' && Number.isSafeInteger(resolutionRevision) && resolutionRevision >= 0
      ? resolutionRevision
      : latestAcknowledgedRevision;
    if (!Number.isSafeInteger(expectedRevision)) {
      this.pendingMutations.unshift(entry);
      return;
    }

    this.mutationInFlight = entry;
    this.notifyMutationQueue();
    this.mutationAckTimer = setTimeout(() => {
      this.mutationAckTimer = null;
      if (this.mutationInFlight?.mutationId !== entry.mutationId) return;
      this.mutationInFlight = null;
      this.pendingMutations.unshift(entry);
      this.notifyMutationQueue();
      console.warn(`[WebSocket] '${entry.event}' ACK timed out; retained for idempotent retry`);
      setTimeout(() => this.flushMutationQueue(), 1000);
    }, this.mutationAckTimeoutMs);

    this.socket.emit(entry.event, { ...entry.data, expectedRevision }, (response = {}) => {
      // An earlier timed-out attempt can ACK after its idempotent retry has
      // already advanced the queue. Never let that stale callback clear the
      // timer owned by the mutation now in flight. date_of_change: 2026-08-25
      if (this.mutationInFlight?.mutationId !== entry.mutationId) return;
      if (this.mutationAckTimer) {
        clearTimeout(this.mutationAckTimer);
        this.mutationAckTimer = null;
      }
      this.mutationInFlight = null;

      if (response.ok) {
        this.setSessionRevision(entry.sessionId, response.revision);
        this.notifyMutationQueue();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('realtime-mutation-ack', {
            detail: { ...response, event: entry.event, mutationId: entry.mutationId, sessionId: entry.sessionId }
          }));
        }
        entry.resolve?.(response);
        this.flushMutationQueue();
        return;
      }

      if (response.code === 'REVISION_CONFLICT') {
        this.setSessionRevision(entry.sessionId, response.revision);
        // Every later local mutation was composed on the same stale state. The
        // authoritative conflict snapshot replaces it, so discard that branch.
        const discarded = this.pendingMutations.filter((candidate) => candidate.sessionId === entry.sessionId);
        this.pendingMutations = this.pendingMutations.filter((candidate) => candidate.sessionId !== entry.sessionId);
        this.notifyMutationQueue();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('realtime-mutation-conflict', { detail: response }));
        }
        const conflictError = new Error(response.message || 'The BattleRoom changed before this update.');
        conflictError.code = response.code;
        conflictError.response = response;
        entry.reject?.(conflictError);
        this.rejectMutationEntries(discarded, conflictError);
        return;
      }

      if (response.code === 'SESSION_NOT_JOINED') {
        // A restarted backend can accept the replacement transport before the
        // React rejoin effect has completed. Retain this idempotent mutation,
        // clear transport-scoped membership, and let the context rejoin before
        // flushing it again. date_of_change: 2026-08-25
        this.currentSessionId = null;
        this.pendingMutations.unshift(entry);
        this.notifyMutationQueue();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('websocket-session-membership-lost', {
            detail: { sessionId: entry.sessionId }
          }));
        }
        return;
      }

      console.error(`[WebSocket] '${entry.event}' rejected:`, response.message || response.code || 'Unknown error');
      this.notifyMutationQueue();
      // Promise-based mutations report failure beside the action that created
      // them. A second global rejection banner previously duplicated that
      // feedback and exposed raw server contracts to players.
      // date_of_change: 2026-09-08
      if (typeof window !== 'undefined' && typeof entry.reject !== 'function') {
        window.dispatchEvent(new CustomEvent('realtime-mutation-rejected', {
          detail: { ...response, event: entry.event, mutationId: entry.mutationId }
        }));
      }
      const rejectedError = new Error(response.message || response.code || `Failed to process ${entry.event}.`);
      rejectedError.code = response.code;
      rejectedError.response = response;
      entry.reject?.(rejectedError);
      this.flushMutationQueue();
    });
  }

  /**
   * Join a combat session room
   * date_of_change: 2026-04-19 - Accept optional alias so the backend can attach a display name
   * to the participant record without revealing the user's real Clerk name.
   * @param {string} sessionId - Combat session ID
   * @param {string} [alias] - Optional display name to show other participants
   */
  joinCombat(sessionId, alias) {
    return new Promise((resolve, reject) => {
      if (!this.isConnected) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      console.log('[WebSocket] Joining combat session:', sessionId, alias ? `as "${alias}"` : '');

      const timeout = setTimeout(() => {
        reject(new Error('Join combat timeout'));
      }, this.joinAckTimeoutMs);

      const errorHandler = (error) => {
        clearTimeout(timeout);
        console.error('[WebSocket] Join combat error:', error);
        this.socket.off('error', errorHandler);
        reject(new Error(error));
      };

      this.socket.once('error', errorHandler);

      // Backend handler signature is (sessionId: string, ack: fn) — send bare string only.
      // Alias is stored client-side in localStorage; backend does not yet accept it.
      this.socket.emit('joinCombat', sessionId, (response) => {
        clearTimeout(timeout);
        this.socket.off('error', errorHandler);

        console.log('[WebSocket] Join ack received:', response);

        if (response && response.ok) {
          console.log('[WebSocket] Successfully joined combat session:', sessionId);
          this.currentSessionId = String(sessionId);
          this.setSessionRevision(sessionId, response.revision);
          this.flushMutationQueue();
          resolve(response);
        } else {
          console.error('[WebSocket] Join combat failed:', response?.error || 'Unknown error');
          reject(new Error(response?.error || 'Join combat failed'));
        }
      });
    });
  }

  /**
   * Leave current combat session
   */
  leaveCombat() {
    const leavingSessionId = this.currentSessionId;
    if (this.socket && this.isConnected) {
      this.socket.emit('leaveCombat');
      console.log('[WebSocket] Left combat session');
    }
    if (leavingSessionId) {
      const abandoned = this.pendingMutations.filter((entry) => entry.sessionId === String(leavingSessionId));
      if (this.mutationInFlight?.sessionId === String(leavingSessionId)) abandoned.unshift(this.mutationInFlight);
      const leaveError = new Error('You left the BattleRoom before queued changes were acknowledged.');
      leaveError.code = 'SESSION_LEFT';
      this.rejectMutationEntries(abandoned, leaveError);
      this.pendingMutations = this.pendingMutations.filter((entry) => entry.sessionId !== String(leavingSessionId));
      if (this.mutationInFlight?.sessionId === String(leavingSessionId)) {
        this.mutationInFlight = null;
        if (this.mutationAckTimer) clearTimeout(this.mutationAckTimer);
        this.mutationAckTimer = null;
      }
      this.sessionRevisions.delete(String(leavingSessionId));
    }
    this.currentSessionId = null;
    this.notifyMutationQueue();
  }

  /**
   * Add connection state callback
   * @param {string} type - Callback type (onConnect, onDisconnect, onReconnect, onError)
   * @param {Function} callback - Callback function
   */
  addConnectionCallback(type, callback) {
    if (this.connectionCallbacks[type]) {
      this.connectionCallbacks[type].push(callback);
    }
  }

  /**
   * Remove connection state callback
   * @param {string} type - Callback type
   * @param {Function} callback - Callback function
   */
  removeConnectionCallback(type, callback) {
    if (this.connectionCallbacks[type]) {
      const index = this.connectionCallbacks[type].indexOf(callback);
      if (index > -1) {
        this.connectionCallbacks[type].splice(index, 1);
      }
    }
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      socketId: this.socket?.id || null,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      pendingMutations: this.pendingMutations.length + (this.mutationInFlight ? 1 : 0)
    };
  }

  /**
   * Get ready promise to wait for connection before operations
   * @date_of_change: 2025-09-26 - Added ready promise getter for race condition prevention
   */
  ready() {
    if (this.isConnected) {
      return Promise.resolve();
    }
    return this.connectionAttemptPromise || Promise.resolve();
  }

  /**
   * Force reconnection attempt
   */
  async forceReconnect() {
    console.log('[WebSocket] Force reconnecting...');
    const reconnectAuth = this.storedAuthData ? { ...this.storedAuthData } : {};

    if (this.socket) {
      this.disconnect({ preserveSession: true });
    }

    // Wait a moment before reconnecting
    await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      await this.connect(reconnectAuth);
      return true;
    } catch (error) {
      console.error('[WebSocket] Force reconnect failed:', error);
      return false;
    }
  }

  /**
   * Connect with resolved backend to ensure consistency
   * Always derives WebSocket URL from the resolved backend base
   * Reconnects if backend base changes
   * @date_of_change: 2025-09-25 - Added for REST/WebSocket consistency
   */
  async connectWithResolvedBackend(auth = {}) {
    const backendBase = await backendUrlResolver.getBackendUrl();

    // Check if backend changed and we need to reconnect
    if (this.currentBackendBase && this.currentBackendBase !== backendBase) {
      console.log('[WebSocket] Backend changed from', this.currentBackendBase, 'to', backendBase);
      this.disconnect({ preserveSession: true });
    }

    // Connect to the current backend
    return this.connect(auth);
  }

  /**
   * Get the current backend base URL for diagnostics
   * @date_of_change: 2025-09-25 - Added for debugging
   */
  getCurrentBackend() {
    return this.currentBackendBase;
  }

  /**
   * Get stored authentication data (for avoiding unnecessary API calls)
   * @date_of_change: 2025-10-01 - Added to optimize reconnection auth
   */
  getStoredAuthData() {
    return this.storedAuthData;
  }
}

// Create singleton instance
const webSocketService = new WebSocketService();

/**
 * Connect socket with resolved backend base
 * Exported helper function for consistency
 * @date_of_change: 2025-09-25 - Added for backend consistency
 */
export async function connectSocketWithResolvedBackend(auth = {}) {
  return webSocketService.connectWithResolvedBackend(auth);
}

/**
 * Get WebSocket URL from HTTP URL with proper protocol handling
 * @date_of_change: 2025-09-25 - Added for protocol consistency
 */
export function httpToWs(url) {
  if (!url) return null;
  return url.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
}

export default webSocketService;
